import { calculateHeaderGroupHeight } from '@theme/critical';
import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element.
 * @property {HTMLElement} headerMenu - The header menu element.
 * @property {HTMLElement} headerRowTop - The header top row element.
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */
class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden.
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position.
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'.
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * Last recorded scrollTop.
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * Timeout for hiding animation, when sticky behavior is 'scroll-up'.
   * @type {number | null}
   */
  #timeout = null;

  /**
   * requestAnimationFrame id for scroll throttling.
   * @type {number | null}
   */
  #scrollFrame = null;

  /**
   * Cached sticky mode.
   * @type {string | null}
   */
  #stickyMode = null;

  /**
   * Cached header height to avoid unnecessary CSS variable writes.
   * @type {number}
   */
  #lastHeaderHeight = 0;

  /**
   * Cached menu hidden state to avoid repeated class writes.
   * @type {boolean | null}
   */
  #menuHidden = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'.
   * @constant {number}
   */
  #animationDelay = 150;

  /**
   * Keeps the global `--header-height` custom property up to date.
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const borderBoxSize = Array.isArray(entry.borderBoxSize) ? entry.borderBoxSize[0] : entry.borderBoxSize;
    const rawHeight = borderBoxSize?.blockSize ?? entry.contentRect?.height ?? 0;
    const height = Math.ceil(rawHeight || 0);

    if (height > 0 && height !== this.#lastHeaderHeight) {
      this.#lastHeaderHeight = height;
      document.body.style.setProperty('--header-height', `${height}px`);
    }

    if (this.#menuDrawerHiddenWidth !== null && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Sets sticky state only when it actually changes.
   *
   * @param {string} value
   * @returns {boolean}
   */
  #setStickyState(value) {
    if (this.dataset.stickyState === value) return false;

    this.dataset.stickyState = value;
    return true;
  }

  /**
   * Sets scroll direction only when it actually changes.
   *
   * @param {string} value
   */
  #setScrollDirection(value) {
    if (this.dataset.scrollDirection === value) return;

    this.dataset.scrollDirection = value;
  }

  /**
   * Observes the header while scrolling the viewport to track when it is sticky.
   *
   * @param {boolean} alwaysSticky
   */
  #observeStickyPosition(alwaysSticky = true) {
    if (this.#intersectionObserver) return;

    this.#intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;

        if (alwaysSticky) {
          const changed = this.#setStickyState(entry.isIntersecting ? 'inactive' : 'active');

          if (changed) {
            changeMetaThemeColor(this.refs.headerRowTop);
          }

          return;
        }

        this.#offscreen = !entry.isIntersecting || this.dataset.stickyState === 'active';
      },
      {
        threshold: alwaysSticky ? 1 : 0,
      }
    );

    this.#intersectionObserver.observe(this);
  }

  /**
   * Handles the overflow minimum event from the header menu.
   *
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer.
   *
   * @param {boolean} hideMenu
   */
  #updateMenuVisibility(hideMenu) {
    if (this.#menuHidden === hideMenu) return;

    this.refs.headerDrawerContainer.classList.toggle('desktop:hidden', !hideMenu);
    this.refs.headerMenu.classList.toggle('hidden', hideMenu);

    this.#menuDrawerHiddenWidth = hideMenu ? window.innerWidth : null;
    this.#menuHidden = hideMenu;
  }

  /**
   * Passive scroll handler. Actual work is throttled to one frame.
   */
  #handleWindowScroll = () => {
    if (this.#scrollFrame !== null) return;

    this.#scrollFrame = window.requestAnimationFrame(() => {
      this.#scrollFrame = null;
      this.#processWindowScroll();
    });
  };

  /**
   * Processes sticky header state during scroll.
   */
  #processWindowScroll() {
    const stickyMode = this.#stickyMode;

    if (!stickyMode) return;
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? window.scrollY ?? 0;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = scrollTop <= 0;

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.#setScrollDirection('none');
      } else {
        this.#setScrollDirection(isScrollingUp ? 'up' : 'down');
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (this.#timeout) {
      window.clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (isScrollingUp) {
      this.removeAttribute('data-animating');

      if (isAtTop) {
        this.#offscreen = false;
        this.#setStickyState('inactive');
        this.#setScrollDirection('none');
      } else {
        this.#setStickyState('active');
        this.#setScrollDirection('up');
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (this.dataset.stickyState === 'active') {
      this.#setScrollDirection('none');

      if (!this.hasAttribute('data-animating')) {
        this.setAttribute('data-animating', '');
      }

      this.#timeout = window.setTimeout(() => {
        this.#setStickyState('idle');
        this.removeAttribute('data-animating');
        this.#timeout = null;
      }, this.#animationDelay);
    } else {
      this.#setScrollDirection('none');
      this.#setStickyState('idle');
    }

    this.#lastScrollTop = scrollTop;
  }

  connectedCallback() {
    super.connectedCallback();

    this.#stickyMode = this.getAttribute('sticky');
    this.#lastScrollTop = document.scrollingElement?.scrollTop ?? window.scrollY ?? 0;
    this.#menuHidden = this.refs.headerMenu.classList.contains('hidden');

    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    if (this.#stickyMode) {
      this.#observeStickyPosition(this.#stickyMode === 'always');

      if (this.#stickyMode === 'scroll-up' || this.#stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;

    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);

    if (this.#scrollFrame !== null) {
      window.cancelAnimationFrame(this.#scrollFrame);
      this.#scrollFrame = null;
    }

    if (this.#timeout) {
      window.clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  if (!headerGroup) return;

  const observedElements = new WeakSet();
  let resizeFrame = null;

  const scheduleHeaderGroupHeightCalculation = () => {
    if (resizeFrame !== null) return;

    resizeFrame = window.requestAnimationFrame(() => {
      resizeFrame = null;
      calculateHeaderGroupHeight(header, headerGroup);
    });
  };

  const resizeObserver = new ResizeObserver(scheduleHeaderGroupHeightCalculation);

  const observeHeaderGroupChildren = () => {
    const children = headerGroup.children;

    for (let i = 0; i < children.length; i++) {
      const element = children[i];

      if (element === header || !(element instanceof HTMLElement) || observedElements.has(element)) {
        continue;
      }

      observedElements.add(element);
      resizeObserver.observe(element);
    }
  };

  observeHeaderGroupChildren();
  scheduleHeaderGroupHeightCalculation();

  const mutationObserver = new MutationObserver((mutations) => {
    let shouldObserve = false;

    for (const mutation of mutations) {
      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        shouldObserve = true;
        break;
      }
    }

    if (!shouldObserve) return;

    observeHeaderGroupChildren();
    scheduleHeaderGroupHeightCalculation();
  });

  mutationObserver.observe(headerGroup, { childList: true });
});